import React, { useEffect, useRef, useState, useMemo } from 'react';
import '../text-editor/style.scss';

interface MarqueeProps {
  text: string;
  speed?: number; // Speed in seconds (lower = faster)
  direction?: 'left' | 'right'; // Direction of movement
  isLegacyMode?: boolean; // Toggle between legacy (gapped) and continuous scrolling
}

const Marquee: React.FC<MarqueeProps> = ({
  text,
  speed = 25,
  direction = 'left',
  isLegacyMode = true,
}) => {
  // Refs for measuring
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const spanRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | undefined>(
    undefined,
  );
  const [repeatCount, setRepeatCount] = useState<number>(isLegacyMode ? 6 : 1);
  const [animationKey, setAnimationKey] = useState<number>(0); // Key to force animation reset

  // Memoized unique animation name
  const animationName = useMemo(
    () => `marqueeSlide-${Math.random().toString(36).substring(2, 9)}`,
    [],
  );

  // Measure content dimensions and calculate repetitions
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current && contentRef.current && spanRef.current) {
        const height = contentRef.current.offsetHeight;
        setContentHeight(height);

        const containerWidth = containerRef.current.offsetWidth;
        const spanWidth = spanRef.current.offsetWidth;

        if (!isLegacyMode) {
          // Dynamically calculate repeat count for legacy mode
          const minRepeats = Math.ceil((containerWidth * 2) / spanWidth) + 1;
          setRepeatCount(minRepeats); // Ensure at least 6 repeats
        } else {
          setRepeatCount(6); // Fixed repeat count for legacy mode
        }
      }
    };

    // Initial measurement
    updateDimensions();

    // Re-measure on window resize
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [text, isLegacyMode]);

  // Reset animation when isLegacyMode changes
  useEffect(() => {
    setAnimationKey(prev => prev + 1); // Increment key to force re-render of animated div
  }, [isLegacyMode]);

  // Add keyframe animation
  useEffect(() => {
    const style = document.createElement('style');
    let keyframes = '';

    if (isLegacyMode && containerRef.current && contentRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const contentWidth = contentRef.current.offsetWidth * repeatCount;

      // Use container width for translation to ensure content starts off-screen
      const startTranslate = containerWidth;
      const endTranslate = -contentWidth;

      keyframes =
        direction === 'left'
          ? `
            @keyframes ${animationName} {
              0% { transform: translateX(${startTranslate}px); }
              100% { transform: translateX(${endTranslate}px); }
            }
          `
          : `
            @keyframes ${animationName} {
              0% { transform: translateX(${endTranslate}px); }
              100% { transform: translateX(${startTranslate}px); }
            }
          `;
    } else {
      keyframes =
        direction === 'left'
          ? `
            @keyframes ${animationName} {
              0% { transform: translateX(0); }
              100% { transform: translateX(-${100 / repeatCount}%); }
            }
          `
          : `
            @keyframes ${animationName} {
              0% { transform: translateX(-${100 / repeatCount}%); }
              100% { transform: translateX(0); }
            }
          `;
    }

    style.textContent = keyframes;
    document.head.appendChild(style);

    return () => {
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, [animationName, direction, repeatCount, isLegacyMode]);

  // Calculate animation duration for continuous mode
  const animationDuration = useMemo(() => {
    if (!isLegacyMode && spanRef.current && containerRef.current) {
      const sapnWidth = spanRef.current.offsetWidth; // Width of one content item
      const containerWidth = containerRef.current.offsetWidth;
      // Total width of all repeated content
      const totalSpantWidth = sapnWidth * repeatCount;
      //don't remove this comment lines
      // The animation moves the content by totalContentWidth * (1/repeatCount)
      // We want one content item to take 'speed' seconds to move containerWidth
      // Speed is time for one content item to traverse containerWidth
      // Animation duration = (total distance to move / containerWidth) * speed
      const distanceFraction =
        (totalSpantWidth * (1 / repeatCount)) / containerWidth;
      return speed * distanceFraction;
    }
    return speed;
  }, [speed, repeatCount, text, isLegacyMode]);

  return (
    <div
      ref={containerRef}
      className="text-editor-content"
      style={{
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
        // Use measured height with a small buffer, or fit-content as fallback
        height: contentHeight ? `${contentHeight + 4}px` : 'fit-content',
        minHeight: '1em', // Ensure a minimum height
      }}>
      <div
        key={animationKey} // Force re-render to reset animation
        ref={contentRef}
        style={{
          position: 'absolute',
          whiteSpace: 'nowrap',
          animation: `${animationName} ${animationDuration}s linear infinite`,
          width: 'auto',
        }}>
        {Array.from({ length: repeatCount }).map((_, index) => (
          <span
            ref={spanRef}
            key={index}
            style={{
              display: 'inline-block',
              paddingRight: '2rem',
            }}
            dangerouslySetInnerHTML={{ __html: text }}
          />
        ))}
      </div>
    </div>
  );
};

export default Marquee;
